import { Box, Link, Tooltip, Typography } from "@mui/material";
import { isURL } from "@utils:general";
import { get } from "lodash";
import type { Components } from "react-markdown";

import { CodeBlock } from "../CodeBlock";
import { MARKDOWN_STYLES, REFERENCE_LINK_PATTERN } from "./constants";
import {
  MdTableBody,
  MdTableCell,
  MdTableHead,
  MdTableRow,
  ResponsiveTable,
} from "./ResponsiveTable";
import { createReferenceMap, type ReferenceDoc } from "./utils";

export const createMarkdownComponents = (
  references: ReferenceDoc[],
): Partial<Components> => {
  const referenceMap = createReferenceMap(references);

  return {
    code: (props) => {
      const { inline, className, children } = props as {
        inline?: boolean;
        className?: string;
        children: React.ReactNode;
      };
      return (
        <CodeBlock inline={inline} className={className}>
          {children}
        </CodeBlock>
      );
    },

    a: ({ href, children, title, ...props }) => {
      try {
        const refMatch = href?.match(REFERENCE_LINK_PATTERN);

        if (refMatch) {
          const num = parseInt(refMatch[1], 10);
          const ref = referenceMap[num];
          const tooltipText = get(
            ref,
            "reference",
            title || href || "",
          ) as string;
          const isLink = isURL(tooltipText);

          if (isLink) {
            return (
              <Tooltip title={tooltipText}>
                <Box
                  component="a"
                  href={tooltipText}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={MARKDOWN_STYLES.referenceLink}
                >
                  {children}
                </Box>
              </Tooltip>
            );
          }

          return (
            <Tooltip title={tooltipText}>
              <Box component="span" sx={MARKDOWN_STYLES.referenceLink}>
                {children}
              </Box>
            </Tooltip>
          );
        }

        return (
          <Link
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            color="primary"
            {...props}
          >
            {children}
          </Link>
        );
      } catch (error) {
        console.error("[MarkdownComponents.a]", error);
        return (
          <Link href={href} {...props}>
            {children}
          </Link>
        );
      }
    },

    p: ({ children }) => (
      <Typography component="p" sx={MARKDOWN_STYLES.paragraph}>
        {children}
      </Typography>
    ),

    h1: ({ children }) => (
      <Typography variant="h3" sx={MARKDOWN_STYLES.heading}>
        {children}
      </Typography>
    ),

    h2: ({ children }) => (
      <Typography variant="h4" sx={MARKDOWN_STYLES.heading}>
        {children}
      </Typography>
    ),

    h3: ({ children }) => (
      <Typography variant="h5" sx={MARKDOWN_STYLES.heading}>
        {children}
      </Typography>
    ),

    h4: ({ children }) => (
      <Typography variant="h6" sx={MARKDOWN_STYLES.heading}>
        {children}
      </Typography>
    ),

    h5: ({ children }) => (
      <Typography
        variant="body1"
        sx={{ ...MARKDOWN_STYLES.heading, fontWeight: 600 }}
      >
        {children}
      </Typography>
    ),

    h6: ({ children }) => (
      <Typography
        variant="body2"
        sx={{ ...MARKDOWN_STYLES.heading, fontWeight: 600 }}
      >
        {children}
      </Typography>
    ),

    ul: ({ children }) => (
      <Box component="ul" sx={MARKDOWN_STYLES.unorderedList}>
        {children}
      </Box>
    ),

    ol: ({ children }) => (
      <Box component="ol" sx={MARKDOWN_STYLES.orderedList}>
        {children}
      </Box>
    ),

    li: ({ children }) => (
      <Box component="li" sx={MARKDOWN_STYLES.listItem}>
        {children}
      </Box>
    ),

    blockquote: ({ children }) => (
      <Box component="blockquote" sx={MARKDOWN_STYLES.blockquote}>
        {children}
      </Box>
    ),

    strong: ({ children }) => (
      <Typography component="strong" sx={{ fontWeight: 700, fontSize: "16px" }}>
        {children}
      </Typography>
    ),

    em: ({ children }) => (
      <Typography component="em" sx={{ fontStyle: "italic", fontSize: "16px" }}>
        {children}
      </Typography>
    ),

    table: ({ children }) => <ResponsiveTable>{children}</ResponsiveTable>,
    thead: ({ children }) => <MdTableHead>{children}</MdTableHead>,
    tbody: ({ children }) => <MdTableBody>{children}</MdTableBody>,
    tr: ({ children }) => <MdTableRow>{children}</MdTableRow>,
    th: ({ children }) => <MdTableCell isHeader>{children}</MdTableCell>,
    td: ({ children }) => <MdTableCell>{children}</MdTableCell>,
  };
};
